/**
 * Context: ProductContext
 * Maneja productos globalmente
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import mockDataService from '../services/mockDataService';
import { generateProductId } from '../utils/helpers';

const ProductContext = createContext(null);

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carga productos al iniciar
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = useCallback(() => {
    setLoading(true);
    try {
      const prods = mockDataService.getProducts();
      setProducts(prods);
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductsByVendor = useCallback((vendorId) => {
    return products.filter(p => p.vendedorId === vendorId && p.disponible);
  }, [products]);

  const createProduct = useCallback((productData, vendorId) => {
    try {
      const product = {
        id: generateProductId(),
        vendedorId: vendorId,
        disponible: true,
        ...productData
      };

      mockDataService.createProduct(product);
      setProducts(prev => [...prev, product]);

      return { success: true, product };
    } catch (error) {
      console.error('Error creando producto:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const updateProduct = useCallback((productId, updates) => {
    try {
      const updated = mockDataService.updateProduct(productId, updates);
      
      if (updated) {
        setProducts(prev => prev.map(p => p.id === productId ? updated : p));
        return { success: true, product: updated };
      }

      return { success: false, error: 'Producto no encontrado' };
    } catch (error) {
      console.error('Error actualizando producto:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const deleteProduct = useCallback((productId) => {
    try {
      const deleted = mockDataService.deleteProduct(productId);
      
      if (deleted) {
        setProducts(prev => prev.filter(p => p.id !== productId));
        return { success: true };
      }

      return { success: false, error: 'Producto no encontrado' };
    } catch (error) {
      console.error('Error eliminando producto:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        getProductsByVendor,
        createProduct,
        updateProduct,
        deleteProduct,
        reloadProducts: loadProducts
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProductContext = () => {
  const context = useContext(ProductContext);
  
  if (!context) {
    throw new Error('useProductContext debe usarse dentro de ProductProvider');
  }
  
  return context;
};

export default ProductContext;
